"use client";

/**
 * CommandPalette — the global ⌘K / Ctrl+K quick switcher, in two tiers.
 *
 * Tier one is local and synchronous: the task history the browser already
 * holds plus a small action set, filtered as the person types. It renders in
 * the same frame the key lands in, which is the whole reason the palette feels
 * instant — it must never move behind an effect or wait on the network.
 *
 * It carries NO sidebar destinations. The palette is a search surface, not a
 * second navigation menu: Home / Abilities / Knowledge / Routines / Connectors
 * live in the sidebar, which is always on screen, and repeating them here cost
 * five rows at the top of every result list for a trip nobody opens ⌘K to make.
 *
 * Tier two is `search:global` over the socket: the whole corpus, debounced,
 * arriving underneath tier one without ever replacing it. A tab strip appears
 * only for the types that actually have hits (ruling T5a) — `All` stays one
 * ranked list, and selecting a type narrows to it.
 *
 * Highlighting a task prewarms it (wakes the sandbox + preloads messages), so
 * pressing Enter opens it warm.
 */

import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  type ReactNode,
  type KeyboardEvent,
} from "react";
import { useRouter } from "next/navigation";
import { useHistoryStore } from "@/stores/historyStore";
import { useTaskHoverPrewarm } from "@/hooks/useTaskHoverPrewarm";
import { useEmbeddedBrowserYield } from "@/hooks/useEmbeddedBrowserYield";
import { useDebounce } from "@/hooks/useDebounce";
import { useGlobalSearch, SEARCH_DEBOUNCE_MS } from "@/hooks/useGlobalSearch";
import { useFocusOnceSettled } from "@/hooks/useFocusOnceSettled";
import { cn } from "@/lib/utils";
import { posthog } from "@/lib/posthog";
import {
  ANALYTICS_EVENTS,
  type SearchHit,
  type SearchType,
} from "@bash/shared";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import Tabs from "@/components/atoms/tabs";
import { SearchIcon } from "@/components/icons/SearchIcon";

type Item =
  | { kind: "task"; id: string; label: string; when?: string }
  | { kind: "hit"; hit: SearchHit };

/** Opens the palette from anywhere. See the listener in CommandPalette. */
export const OPEN_COMMAND_PALETTE = "vestra:open-command-palette";

export function openCommandPalette(): void {
  window.dispatchEvent(new Event(OPEN_COMMAND_PALETTE));
}

const MAX_TASKS = 8;

/** A selected type tab pages deeper than the broad call's 5 per group. */
const TAB_LIMIT = 25;

// ── The tab strip ──────────────────────────────────────────────────────────
// `Abilities` merges skill + agent + connector: it is the sidebar's own word
// for /library, and three tabs for rarely-searched config would crowd a strip
// whose whole job is to read at a glance.

type TabKey = "all" | "task" | "artifact" | "routine" | "ability" | "memory";

const TYPE_TABS: {
  key: Exclude<TabKey, "all">;
  label: string;
  types: SearchType[];
}[] = [
  { key: "task", label: "Tasks", types: ["task"] },
  { key: "artifact", label: "Artifacts", types: ["artifact"] },
  { key: "routine", label: "Routines", types: ["routine"] },
  {
    key: "ability",
    label: "Abilities",
    types: ["skill", "agent", "connector"],
  },
  { key: "memory", label: "Memory", types: ["memory"] },
];

// ── Icons ──────────────────────────────────────────────────────────────────
// One quiet stroke icon per type, because `All` is a mixed list and the row
// needs a signal that survives repetition where an uppercase text chip did not.

type IconName =
  | "task"
  | "artifact"
  | "media"
  | "routine"
  | "ability"
  | "connector"
  | "memory"
  | "action";

const ICONS: Record<IconName, ReactNode> = {
  task: <path d="M21 12a8 8 0 01-11.5 7.2L3 21l1.8-6.5A8 8 0 1121 12z" />,
  artifact: (
    <>
      <path d="M14 3H7a1 1 0 00-1 1v16a1 1 0 001 1h10a1 1 0 001-1V7z" />
      <path d="M14 3v4h4" />
    </>
  ),
  routine: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  ability: (
    <path d="M12 3l2.4 5.3 5.6.7-4.2 3.9 1.1 5.6L12 15.9 7.1 18.5l1.1-5.6L4 9l5.6-.7z" />
  ),
  connector: (
    <>
      <path d="M9 3v6M15 3v6" />
      <path d="M6 9h12v3a6 6 0 01-12 0z" />
      <path d="M12 18v3" />
    </>
  ),
  media: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="M3 17l5-4 4 3 3-2 6 5" />
    </>
  ),
  memory: <path d="M6 4a1 1 0 011-1h10a1 1 0 011 1v17l-6-4-6 4z" />,
  action: <path d="M12 5v14M5 12h14" />,
};

/** Media kinds that read as a picture rather than a document. */
const MEDIA_KINDS = new Set(["image", "video"]);

const HIT_ICON: Record<SearchType, IconName> = {
  task: "task",
  artifact: "artifact",
  routine: "routine",
  skill: "ability",
  agent: "ability",
  connector: "connector",
  memory: "memory",
};

/**
 * ts_headline wraps the matched run in <b>…</b>. The snippet is user-authored
 * content, so it never reaches an HTML sink: the tags are parsed out and every
 * other character becomes a React text node, which React escapes.
 */
function snippetNodes(snippet: string): ReactNode[] {
  const bold = /<b>([\s\S]*?)<\/b>/g;
  const nodes: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = bold.exec(snippet)) !== null) {
    if (match.index > last) nodes.push(snippet.slice(last, match.index));
    nodes.push(
      <b key={match.index} className="font-semibold text-fg-muted">
        {match[1]}
      </b>
    );
    last = match.index + match[0].length;
  }
  if (last < snippet.length) nodes.push(snippet.slice(last));
  return nodes;
}

/** Today, then days inside the week, then a calendar date. */
function whenLabel(iso: string | undefined, now: number): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const midnight = new Date(now).setHours(0, 0, 0, 0);
  if (t >= midnight) return "Today";
  const days = Math.ceil((midnight - t) / 86_400_000);
  if (days <= 6) return `${days}d`;
  return new Date(t).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

interface Row {
  key: string;
  icon: IconName;
  title: string;
  snippet: string | null;
  when?: string;
  /** Artifact rows only: the preview the mock paints at the end of the row. */
  thumbUrl?: string;
  /** Set on task rows only — hovering one prewarms its sandbox. */
  prewarmId?: string;
}

function rowOf(item: Item): Row {
  switch (item.kind) {
    case "task":
      return {
        key: `task:${item.id}`,
        icon: "task",
        title: item.label,
        snippet: null,
        when: item.when,
        prewarmId: item.id,
      };
    case "hit":
      return {
        key: `hit:${item.hit.type}:${item.hit.id}`,
        // An artifact is a picture or a document depending on its kind, and
        // one icon for both would say neither.
        icon:
          item.hit.type === "artifact" && MEDIA_KINDS.has(item.hit.kind ?? "")
            ? "media"
            : (HIT_ICON[item.hit.type] ?? "action"),
        title: item.hit.title || "Untitled",
        snippet: item.hit.snippet,
        when: item.hit.when,
        thumbUrl: item.hit.thumbUrl,
        prewarmId: item.hit.type === "task" ? item.hit.id : undefined,
      };
  }
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [tab, setTab] = useState<TabKey>("all");
  // The "when" column reads off one timestamp captured per opening, so a row's
  // label cannot shift under the person mid-session and render stays pure.
  const [openedAt, setOpenedAt] = useState(() => Date.now());
  const router = useRouter();
  const taskHistory = useHistoryStore((s) => s.taskHistory);
  const { onHoverStart } = useTaskHoverPrewarm();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);
  // The palette overlays the whole app — the embedded browser's native pane
  // must step back while it is open (no-op outside the desktop shell).
  useEmbeddedBrowserYield(open);
  useFocusOnceSettled(open, dropdownContentRef, () =>
    inputRef.current?.focus()
  );

  // Tier two. Debounced at this call site, not inside the hook, so a keystroke
  // costs a round trip only once the person stops typing. A closed palette asks
  // nothing at all — otherwise the last query stays live and react-query
  // refetches it on the next window focus, for a list nobody is looking at.
  const debouncedQuery = useDebounce(query.trim(), SEARCH_DEBOUNCE_MS);
  const { data: server, isFetching } = useGlobalSearch(
    open ? debouncedQuery : ""
  );
  // The STRIP reads the same broad query through a second observer that keeps
  // the previous answer while the next one is in flight — same key, so it is a
  // second view of one round trip, not a second call. Without it the strip
  // blinks out on every keystroke pause (data goes undefined on a key change)
  // and the selected tab reverts to All, which also un-narrows the deep read.
  // The ROWS keep reading `server`, which stays undefined until the answer for
  // THIS query lands: a superseded response must never reach the list.
  const { data: stripServer } = useGlobalSearch(
    open ? debouncedQuery : "",
    undefined,
    undefined,
    { keepPrevious: true }
  );

  // ⌘K / Ctrl+K toggles; Escape closes.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    // The sidebar's search button opens the palette through this event rather
    // than synthesising a ⌘K keystroke. A fake KeyboardEvent would toggle
    // (closing an already-open palette), carries no meaning to a reader, and
    // breaks the moment the shortcut changes; a named event says what it wants.
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_COMMAND_PALETTE, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_COMMAND_PALETTE, onOpen);
    };
  }, []);

  // Set the open timestamp when opening, and reset state AFTER the closing
  // animation completes, so the user doesn't see the UI flash empty while fading out.
  useEffect(() => {
    if (open) {
      setOpenedAt(Date.now());
      // If it reopens before the close timeout finishes, the timeout is cleared.
      // Reset state here to ensure a fresh palette on reopen.
      setQuery("");
      setActiveIndex(0);
      setTab("all");
    } else {
      const t = setTimeout(() => {
        setQuery("");
        setActiveIndex(0);
        setTab("all");
      }, 600); // Wait for the 600ms fade-out
      return () => clearTimeout(t);
    }
  }, [open]);

  const typed = query.trim();

  // Tier one: local, synchronous, and the same list it has always been.
  const items = useMemo<Item[]>(() => {
    const q = query.trim().toLowerCase();
    const match = (s: string) => !q || s.toLowerCase().includes(q);
    const tasks: Item[] = taskHistory
      .filter((t) => match(t.title || "Untitled task"))
      .slice(0, MAX_TASKS)
      .map((t) => ({
        kind: "task",
        id: t.id,
        label: t.title || "Untitled task",
        when: t.lastActivityAt || t.updatedAt,
      }));
    return tasks;
  }, [query, taskHistory]);

  // EVERY type gets a tab, including the ones with nothing in them.
  //
  // This reverses ruling C5, which showed a tab only for types that HAD a
  // group, to keep a strip of zeros off the screen. That was the wrong trade.
  // Membership that changes per keystroke moves the tabs under the pointer —
  // `Artifacts` is third for one keystroke and gone the next — and a target
  // that moves while you aim at it is worse than a target reading 0. A stable
  // strip also means a selected tab is never yanked back to All mid-type.
  //
  // The count still tells the truth: a type with no group counts 0, and
  // selecting it shows the empty note rather than hiding the tab.
  const tabs = useMemo(() => {
    if (!typed) return [];
    // Emptying the box clears the strip at once — the lag is only ever across a
    // refetch, never across "there is nothing to search for".
    // Before the first response there is no count to show, and printing 0
    // would state something false for the length of a round trip — every tab
    // reading "0" a beat before it reads "12". The tab still takes its place
    // (that is the whole point); only the number waits for an answer.
    const answered = stripServer !== undefined;
    const groups = stripServer?.groups ?? [];
    const present = TYPE_TABS.map((t) => {
      const mine = groups.filter((g) => t.types.includes(g.type));
      return {
        id: t.key as TabKey,
        label: t.label,
        badge: answered
          ? mine.reduce((n, g) => n + (g.total ?? 0), 0)
          : undefined,
      };
    });
    return [
      { id: "all" as TabKey, label: "All", badge: undefined },
      ...present,
    ];
  }, [stripServer, typed]);

  // Every tab exists while a query is typed, so this only ever fires when the
  // box empties and the strip goes away underneath a selection.
  const activeTab = tabs.some((t) => t.id === tab) ? tab : "all";

  // Selecting a type re-asks the rail for that type ALONE, and deeper: the
  // broad call pages every group at 5, so a tab reading "Tasks 12" would
  // otherwise show five rows and look broken. The strip and the All list keep
  // reading the broad response, so a narrowed answer can never shrink the strip
  // to the one type it asked about.
  const narrowTypes =
    activeTab === "all"
      ? undefined
      : TYPE_TABS.find((t) => t.key === activeTab)?.types;
  const { data: narrowServer, isFetching: narrowFetching } = useGlobalSearch(
    open && narrowTypes ? debouncedQuery : "",
    narrowTypes,
    TAB_LIMIT
  );

  const serverItems = useMemo<Item[]>(() => {
    const chosen: SearchType[] | null =
      activeTab === "all"
        ? null
        : (TYPE_TABS.find((t) => t.key === activeTab)?.types ?? null);
    // Until the deeper page lands, the broad response's slice of the same type
    // stands in, so a tab click never blanks the list.
    const source = chosen === null ? server : (narrowServer ?? server);
    if (!source) return [];
    // Only `All` shows the local list above, so only `All` can repeat it.
    const localTaskIds =
      activeTab === "all"
        ? new Set(items.filter((i) => i.kind === "task").map((i) => i.id))
        : new Set<string>();

    const seen = new Set<string>();
    const out: Item[] = [];
    const take = (hit: SearchHit) => {
      const key = `${hit.type}:${hit.id}`;
      if (seen.has(key)) return;
      if (hit.type === "task" && localTaskIds.has(hit.id)) return;
      seen.add(key);
      out.push({ kind: "hit", hit });
    };

    if (chosen === null) {
      // One ranked list: the fused `top` block first, then what is left of each
      // group behind it.
      source.top.forEach(take);
      source.groups.forEach((g) => g.hits.forEach(take));
    } else {
      source.groups
        .filter((g) => chosen.includes(g.type))
        .forEach((g) => g.hits.forEach(take));
    }
    return out;
  }, [server, narrowServer, items, activeTab]);

  const visible = useMemo<Item[]>(
    () => (activeTab === "all" ? [...items, ...serverItems] : serverItems),
    [activeTab, items, serverItems]
  );

  // Clamped at READ time as the result set shrinks, not by a setState in an
  // effect: that effect cost a SECOND commit on every keystroke (the row was
  // already on screen after the first), and a derived value cannot go stale.
  const active = Math.min(activeIndex, Math.max(0, visible.length - 1));

  const select = useCallback(
    (item: Item | undefined) => {
      if (!item) return;
      // A search is "performed" when the user acts on a typed query. Bare
      // navigation with no query is not a search, so we gate on it.
      const q = query.trim();
      if (q) {
        posthog.capture(ANALYTICS_EVENTS.SEARCH_PERFORMED, {
          query_length: q.length,
          result_count: visible.length,
          result_kind: item.kind === "hit" ? item.hit.type : item.kind,
          context: "command_palette",
        });
      }
      setOpen(false);
      // Two kinds left, and the union says so: a recent task, or a rail hit
      // carrying the href the service derived. The third branch here used to
      // route `New task` and the sidebar destinations.
      if (item.kind === "task") router.push(`/task/${item.id}`);
      else router.push(item.hit.href);
    },
    [router, query, visible]
  );

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(Math.min(active + 1, visible.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(Math.max(active - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      select(visible[active]);
    }
  };

  if (typeof document === "undefined") return null;

  const searching = isFetching || narrowFetching;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/*
       * The shell is the shared Dialog (#3495) — it owns the portal, the
       * overlay, the height cap and the mobile sheet. Three overrides, and the
       * reason for each:
       *
       * `overflow-hidden` moves the SCROLL down to the list. DialogContent
       * scrolls itself by default, and a scroller wrapped around a scroller is
       * exactly the bug #3495 went after; here the input, the strip and the
       * footer are chrome that must stay put while the results move, so the
       * one scroller belongs to the <ul>.
       *
       * `p-0` because this palette is edge-to-edge: the rows run to the border,
       * the list carries a rule above it and the footer is a full-bleed bar.
       *
       * `hideCloseButton` — the escape hatch the same PR added to the primitive
       * — because the palette has no ✕: esc closes it and the footer says so,
       * and the button would land on top of the search row.
       */}
      <DialogContent
        ref={dropdownContentRef}
        aria-label="Command palette"
        hideCloseButton
        // ONE height, whatever is inside. Recents, results and the empty note
        // are all this tall, so opening the palette and typing into it never
        // resizes the box under the pointer. The list below is the only thing
        // that scrolls (`flex-1 min-h-0`); on mobile the sheet keeps its own
        // sizing, so the fixed height is `sm:` and up.
        className="sm:max-w-[620px] h-[calc(100dvh-2rem)] sm:h-[520px] p-0 xs:p-0 flex flex-col overflow-hidden"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          dropdownContentRef.current?.focus();
        }}
      >
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border-subtle">
          <SearchIcon className="shrink-0 w-4.5 h-4.5 text-fg-subtle" />
          <input
            ref={inputRef}
            className="flex-1 min-w-0 text-4 text-fg-default bg-transparent border-none outline-none placeholder:text-fg-subtle"
            placeholder="Search everything"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onInputKeyDown}
            aria-label="Command palette search"
          />
        </div>

        {tabs.length > 0 && (
          <Tabs
            layout={3}
            active={activeTab}
            items={tabs}
            onTabChange={(id) => {
              setTab(id);
              setActiveIndex(0);
            }}
          />
        )}

        <ul
          className="[list-style:none] m-0 py-2 px-2 flex-1 min-h-0 overflow-y-auto"
          role="listbox"
        >
          {visible.length === 0 && !searching ? (
            <li className="h-full flex flex-col items-center justify-center gap-2 text-fg-faint">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.7}
                strokeLinecap="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" />
              </svg>
              <span className="text-3.5">No results</span>
            </li>
          ) : (
            <>
              {/* Recents get a label; search results do not. The strip above
                  already says what a typed list is, and a second header under
                  it would be the same word twice. `aria-hidden` keeps this out
                  of the listbox's option count — it is a caption, not a row. */}
              {!typed && (
                <li
                  aria-hidden="true"
                  className="px-3 pt-2 pb-1 text-3 text-fg-subtle"
                >
                  Recent tasks
                </li>
              )}
              {visible.map((item, i) => {
                const r = rowOf(item);
                const on = i === active;
                return (
                  <li
                    key={r.key}
                    role="option"
                    aria-selected={on}
                    className={cn(
                      "flex items-center gap-3 py-2.5 px-3 rounded-12 cursor-pointer text-fg-default",
                      on && "bg-bg-muted"
                    )}
                    onMouseEnter={() => {
                      setActiveIndex(i);
                      if (r.prewarmId) onHoverStart(r.prewarmId);
                    }}
                    onClick={() => select(item)}
                  >
                    <svg
                      className={cn(
                        "shrink-0 w-5 h-5",
                        on ? "text-fg-muted" : "text-fg-faint"
                      )}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.7}
                      aria-hidden="true"
                    >
                      {ICONS[r.icon]}
                    </svg>
                    <span className="flex-1 min-w-0">
                      <span className="block text-3.5 font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                        {r.title}
                      </span>
                      {r.snippet && (
                        <span className="block mt-0.5 text-3.5 text-fg-subtle overflow-hidden text-ellipsis whitespace-nowrap">
                          {snippetNodes(r.snippet)}
                        </span>
                      )}
                    </span>
                    {r.thumbUrl && (
                      // A CDN thumbnail on an overlay row: next/image would add
                      // a loader round trip to a list that must paint at once.
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        className="shrink-0 w-8 h-8 rounded-8 object-cover bg-bg-muted border border-border-subtle"
                        src={r.thumbUrl}
                        alt=""
                      />
                    )}
                    {typed && whenLabel(r.when, openedAt) && (
                      <span className="shrink-0 text-3 text-fg-faint tabular-nums">
                        {whenLabel(r.when, openedAt)}
                      </span>
                    )}
                  </li>
                );
              })}
            </>
          )}
          {searching && (
            <li className="flex items-center gap-2.5 py-2.5 px-3 text-3 text-fg-subtle">
              <span className="w-1.5 h-1.5 rounded-full bg-fg-faint animate-pulse motion-reduce:animate-none" />
              Searching everything else
            </li>
          )}
        </ul>

        <div className="hidden can-hover:flex gap-4 px-4 py-2.5 border-t border-border-subtle bg-bg-subtle text-3 text-fg-subtle">
          <span className="flex items-center gap-1.5">
            <Key>↑</Key>
            <Key>↓</Key> move
          </span>
          <span className="flex items-center gap-1.5">
            <Key>↵</Key> open
          </span>
          <span className="flex items-center gap-1.5">
            <Key>esc</Key> close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Key({ children }: { children: ReactNode }) {
  return (
    <kbd className="min-w-4 px-1 text-center text-2.5 rounded-4 border border-border-muted border-b-2 bg-bg-elevated text-fg-muted">
      {children}
    </kbd>
  );
}
